import BigNumber from 'bignumber.js';
import { z } from 'zod';
import { CompanyFixedAssetSpecies, FixedAsset, FixedAssetDraft } from '~/types/fixed-asset';
import { VATEntryFixedAssetProposal } from '~/types/vat-bookkeeping';
import { parseAPIDate } from '~/utils/date';
import { formatDateForAPI } from '~/utils/formatDate';
import { parseOptionalValue } from '~/utils/numberInputs';

export const ACQUISITION_AFTER_SERVICE_START = 'acquisition_date_after_service_start_date';
const REQUIRED_FIELD = 'required_field';

export interface DeductibilityRegimeConfig {
  value: string;
  labelKey: string;
  rate: string;
  cap: string;
}

export const DEDUCTIBILITY_REGIME_OTHER = 'OTHER';
const DEDUCTIBILITY_REGIME_FULLY_DEDUCTIBLE = 'FULLY_DEDUCTIBLE';

export const DEDUCTIBILITY_REGIMES: DeductibilityRegimeConfig[] = [
  {
    value: 'MOTOR_VEHICLES_MIXED_USE_COMPANY',
    labelKey: 'deductibility_regime_vehicles_mixed_use',
    rate: '20',
    cap: '18075,99',
  },
  {
    value: 'MOTOR_VEHICLES_SALES_AGENTS',
    labelKey: 'deductibility_regime_vehicles_agents',
    rate: '80',
    cap: '25822,84',
  },
  {
    value: 'MOTOR_VEHICLES_EMPLOYEE_USE',
    labelKey: 'deductibility_regime_vehicles_employees',
    rate: '70',
    cap: '',
  },
  {
    value: 'MOTOR_VEHICLES_FRINGE_BENEFIT',
    labelKey: 'deductibility_regime_vehicles_directors',
    rate: '20',
    cap: '18075,99',
  },
  {
    value: 'MOTORCYCLES_MIXED_USE_COMPANY',
    labelKey: 'deductibility_regime_motorcycles_mixed_use',
    rate: '20',
    cap: '4131,66',
  },
  {
    value: 'MOTORCYCLES_SALES_AGENTS',
    labelKey: 'deductibility_regime_motorcycles_agents',
    rate: '80',
    cap: '4131,66',
  },
  {
    value: 'MOTORCYCLES_EMPLOYEE_USE',
    labelKey: 'deductibility_regime_motorcycles_employees',
    rate: '70',
    cap: '',
  },
  {
    value: 'MOTORCYCLES_FRINGE_BENEFIT',
    labelKey: 'deductibility_regime_motorcycles_directors',
    rate: '20',
    cap: '4131,66',
  },
  {
    value: 'MOPEDS_MIXED_USE_COMPANY',
    labelKey: 'deductibility_regime_mopeds_mixed_use',
    rate: '20',
    cap: '2065,83',
  },
  { value: 'MOPEDS_SALES_AGENTS', labelKey: 'deductibility_regime_mopeds_agents', rate: '80', cap: '2065,83' },
  { value: 'MOPEDS_EMPLOYEE_USE', labelKey: 'deductibility_regime_mopeds_employees', rate: '70', cap: '' },
  {
    value: 'MOPEDS_FRINGE_BENEFIT',
    labelKey: 'deductibility_regime_mopeds_directors',
    rate: '20',
    cap: '2065,83',
  },
  { value: 'TELEPHONY', labelKey: 'deductibility_regime_telephony', rate: '80', cap: '' },
  {
    value: DEDUCTIBILITY_REGIME_FULLY_DEDUCTIBLE,
    labelKey: 'deductibility_regime_fully_deductible',
    rate: '100',
    cap: '',
  },
  { value: DEDUCTIBILITY_REGIME_OTHER, labelKey: 'deductibility_regime_other', rate: '', cap: '' },
];

const DEFAULT_DEDUCTIBILITY_RATE =
  DEDUCTIBILITY_REGIMES.find((regime) => regime.value === DEDUCTIBILITY_REGIME_FULLY_DEDUCTIBLE)?.rate ?? '100';

/**
 * Validation for a single fixed asset, expressed over the modal's form-value shape (date objects,
 * string amounts/rates, group/species/category keyed by id/code). This is the single source of
 * truth for fixed-asset validation: the modal uses it as its resolver and VATRegistrationSchema
 * reuses it (wrapped in an array on each VAT entry).
 */
export const FixedAssetSchema = z
  .object({
    group: z.string().nullable(),
    species: z.string().nullable(),
    category: z.string().nullable(),
    // Global species-category id (`fixed_asset_species_categories` PK) carried straight from the
    // AI proposal / a saved asset. It is the value the backend actually persists, so the payload
    // prefers it over re-deriving from the (group/species/category) selects — which stays correct
    // even when the company catalog can't render the category label (e.g. its `category` relation
    // is not hydrated). Reset whenever the user changes group/species so it can't go stale.
    speciesCategoryId: z.string().nullable(),
    assetType: z.enum(['TANGIBLE', 'INTANGIBLE']).nullable(),
    description: z.string(),
    serviceStartDate: z.date().nullable(),
    acquisitionDate: z.date().nullable(),
    purchasePrice: z.string(),
    acquisitionType: z.enum(['NEW', 'USED', 'LEASING_BUYOUT']).nullable(),
    bookDepreciationRate: z.string(),
    taxDepreciationRate: z.string(),
    deductibilityRegime: z.string().nullable(),
    deductibilityRate: z.string(),
    deductibilityCap: z.string(),
    notes: z.string().nullable(),
  })
  .superRefine((data, ctx) => {
    const requiredStringFields: (keyof FixedAssetFormValues)[] = [
      'group',
      'species',
      'category',
      'description',
      'acquisitionType',
      'bookDepreciationRate',
      'taxDepreciationRate',
      'deductibilityRegime',
      'deductibilityRate',
    ];
    requiredStringFields.forEach((field) => {
      const value = data[field];
      if (value == null || value === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: REQUIRED_FIELD });
      }
    });
    (['acquisitionDate', 'serviceStartDate'] as const).forEach((field) => {
      if (!data[field]) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: REQUIRED_FIELD });
      }
    });
    if (data.acquisitionDate && data.serviceStartDate && data.acquisitionDate > data.serviceStartDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['acquisitionDate'],
        message: ACQUISITION_AFTER_SERVICE_START,
      });
    }
  });

export type FixedAssetFormValues = z.infer<typeof FixedAssetSchema>;

export const emptyDefaultValues: FixedAssetFormValues = {
  group: null,
  species: null,
  category: null,
  speciesCategoryId: null,
  assetType: null,
  description: '',
  serviceStartDate: null,
  acquisitionDate: null,
  purchasePrice: '',
  acquisitionType: 'NEW',
  bookDepreciationRate: '',
  taxDepreciationRate: '',
  deductibilityRegime: DEDUCTIBILITY_REGIME_FULLY_DEDUCTIBLE,
  deductibilityRate: DEFAULT_DEDUCTIBILITY_RATE,
  deductibilityCap: '',
  notes: null,
};

export const formatAmount = (value?: number | null): string => (value == null ? '' : String(value));

export const fractionToPercent = (fraction?: number): number | undefined =>
  fraction == null ? undefined : new BigNumber(fraction).times(100).toNumber();

const percentToFraction = (percent?: number): number | undefined =>
  percent == null ? undefined : new BigNumber(percent).div(100).toNumber();

/**
 * The draft stores the group/species/category as names (the API payload shape), while the
 * selects are keyed by id/code. Resolve the form values back from the company species catalog.
 */
const resolveSelectValues = (draft: FixedAssetDraft, companySpecies: CompanyFixedAssetSpecies[]) => {
  const matchedSpecies =
    (draft.speciesCategoryId != null
      ? companySpecies.find((cs) => cs.species.speciesCategories?.some((sc) => sc.id === draft.speciesCategoryId))
      : undefined) ??
    companySpecies.find(
      (cs) =>
        cs.species.name === draft.speciesName &&
        (draft.groupName == null || cs.species.group?.name === draft.groupName),
    );
  const group =
    matchedSpecies?.species.group?.id ??
    companySpecies.find((cs) => cs.species.group?.name === draft.groupName)?.species.group?.id ??
    null;
  const species = matchedSpecies?.species.id ?? null;
  const matchedCategory = matchedSpecies?.species.speciesCategories?.find((sc) =>
    draft.speciesCategoryId != null ? sc.id === draft.speciesCategoryId : sc.category?.name === draft.categoryName,
  );
  const category = matchedCategory?.category?.code ?? null;
  return { group, species, category };
};

/**
 * Builds the modal's form values. With a draft (edit) it resolves the group/species/category
 * selects from the species catalog; without one it returns the empty defaults, optionally seeded
 * with a suggested book value for a new asset.
 */
export const buildDefaultValues = (
  draft: FixedAssetDraft | undefined,
  companySpecies: CompanyFixedAssetSpecies[],
  defaultBookValue?: number,
): FixedAssetFormValues => {
  if (!draft) return { ...emptyDefaultValues, purchasePrice: formatAmount(defaultBookValue) };
  const { group, species, category } = resolveSelectValues(draft, companySpecies);
  return {
    group,
    species,
    category,
    speciesCategoryId: draft.speciesCategoryId ?? null,
    assetType: draft.assetType ?? null,
    description: draft.description ?? '',
    serviceStartDate: draft.serviceStartDate ? parseAPIDate(draft.serviceStartDate) : null,
    acquisitionDate: draft.acquisitionDate ? parseAPIDate(draft.acquisitionDate) : null,
    purchasePrice: formatAmount(draft.originalBookValue ?? defaultBookValue),
    acquisitionType: draft.acquisitionType ?? emptyDefaultValues.acquisitionType,
    bookDepreciationRate: formatAmount(fractionToPercent(draft.bookDepreciationRate)),
    taxDepreciationRate: formatAmount(fractionToPercent(draft.taxDepreciationRate)),
    deductibilityRegime: draft.taxDeductibilityType ?? null,
    deductibilityRate: formatAmount(fractionToPercent(draft.taxDeductibilityRate)),
    deductibilityCap: formatAmount(draft.taxDeductibilityCap),
    notes: draft.notes ?? null,
  };
};

const parseBackendNumber = (value?: string | number | null): number | undefined => {
  if (value == null || value === '') return undefined;
  const parsed = new BigNumber(value);
  return parsed.isFinite() ? parsed.toNumber() : undefined;
};

export const buildEditDefaultValues = (
  asset: FixedAsset,
  companySpecies: CompanyFixedAssetSpecies[],
): FixedAssetFormValues => {
  const draft: FixedAssetDraft = {
    description: asset.description,
    speciesCategoryId: asset.speciesCategory?.id,
    groupName: asset.speciesCategory?.species?.group?.name,
    speciesName: asset.speciesCategory?.species?.name,
    categoryName: asset.speciesCategory?.category?.name,
    assetType: asset.assetType,
    depreciationType: asset.depreciationType,
    referenceDepreciationRate: parseBackendNumber(asset.referenceDepreciationRate),
    acquisitionDate: asset.acquisitionDate,
    serviceStartDate: asset.serviceStartDate,
    originalBookValue: parseBackendNumber(asset.initialBookValue.amount),
    acquisitionType: asset.acquisitionType,
    bookDepreciationRate: parseBackendNumber(asset.bookDepreciationRate),
    taxDepreciationRate: parseBackendNumber(asset.taxDepreciationRate),
    taxDeductibilityType: asset.taxDeductibilityType,
    taxDeductibilityRate: parseBackendNumber(asset.taxDeductibilityRate),
    taxDeductibilityCap: parseBackendNumber(asset.taxDeductibilityCap?.amount) ?? null,
    notes: asset.notes ?? null,
  };
  return buildDefaultValues(draft, companySpecies);
};

/**
 * Builds the form values for a fixed asset suggested by the AI registration proposal, which
 * carries only the classification ids (group/species/category/speciesCategory). Resolves the
 * selects and the category-derived fields (asset type, depreciation rates) from the company
 * species catalog, mirroring what the modal applies on category selection; book value and dates
 * follow the same defaults used for a new asset.
 */
export const buildProposalDefaultValues = (
  proposal: VATEntryFixedAssetProposal,
  companySpecies: CompanyFixedAssetSpecies[],
  defaultBookValue?: number,
  defaultDate?: Date | null,
  description?: string,
): FixedAssetFormValues => {
  const matchedSpecies =
    companySpecies.find((cs) => cs.species.id === proposal.speciesId) ??
    companySpecies.find((cs) =>
      cs.species.speciesCategories?.some(
        (sc) =>
          (proposal.categoryId != null && sc.categoryId === proposal.categoryId) ||
          (proposal.speciesCategoryId != null && sc.id === proposal.speciesCategoryId),
      ),
    );
  const matchedCategory = matchedSpecies?.species.speciesCategories?.find(
    (sc) =>
      (proposal.categoryId != null && sc.categoryId === proposal.categoryId) ||
      (proposal.speciesCategoryId != null && sc.id === proposal.speciesCategoryId),
  );
  const categoryRate = formatAmount(fractionToPercent(matchedCategory?.rate));

  return {
    ...emptyDefaultValues,
    description: description ?? '',
    group: matchedSpecies?.species.group?.id ?? matchedSpecies?.species.groupId ?? proposal.groupId,
    species: matchedSpecies?.species.id ?? null,
    category: matchedCategory?.category?.code ?? null,
    // Persisted straight through: the proposal's own speciesCategoryId is the id the backend
    // stores, so the category is saved correctly even if `matchedCategory` couldn't resolve
    // the display code from an incomplete catalog.
    speciesCategoryId: matchedCategory?.id ?? proposal.speciesCategoryId ?? null,
    assetType: matchedCategory?.category?.assetType ?? null,
    bookDepreciationRate: categoryRate,
    taxDepreciationRate: categoryRate,
    purchasePrice: formatAmount(defaultBookValue),
    acquisitionDate: defaultDate ?? null,
    serviceStartDate: defaultDate ?? null,
  };
};

const parseAmount = (value: string): number | undefined => parseOptionalValue(value) ?? undefined;

/**
 * Converts validated form values into the backend-shaped {@link FixedAssetDraft}, resolving the
 * species catalog metadata (names, speciesCategoryId, assetType, depreciation type/rate) from the
 * selected group/species/category. Used when turning the registration form into its API payload.
 */
export const buildDraft = (
  values: FixedAssetFormValues,
  companySpecies: CompanyFixedAssetSpecies[],
): FixedAssetDraft => {
  const species = companySpecies.find((cs) => cs.species.id === values.species);
  const speciesCategory = species?.species.speciesCategories?.find((sc) => sc.category?.code === values.category);
  const assetType = speciesCategory?.category?.assetType;
  const acquisitionType = values.acquisitionType ?? undefined;
  const cap = parseOptionalValue(values.deductibilityCap);

  return {
    description: values.description || undefined,
    groupName: species?.species.group?.name,
    speciesName: species?.species.name,
    categoryName: speciesCategory?.category?.name,
    // Prefer the id resolved from the current selection; fall back to the id threaded from the
    // proposal / saved asset so the category persists even when the catalog couldn't resolve it.
    speciesCategoryId: speciesCategory?.id ?? values.speciesCategoryId ?? undefined,
    assetType,
    depreciationType: speciesCategory?.category?.depreciationType,
    referenceDepreciationRate: speciesCategory?.rate,
    acquisitionDate: values.acquisitionDate ? formatDateForAPI(values.acquisitionDate) : undefined,
    serviceStartDate: values.serviceStartDate ? formatDateForAPI(values.serviceStartDate) : undefined,
    originalBookValue: parseAmount(values.purchasePrice),
    acquisitionType,
    bookDepreciationRate: percentToFraction(parseAmount(values.bookDepreciationRate)),
    taxDepreciationRate: percentToFraction(parseAmount(values.taxDepreciationRate)),
    taxDeductibilityType: values.deductibilityRegime ?? undefined,
    taxDeductibilityRate: percentToFraction(parseAmount(values.deductibilityRate)),
    taxDeductibilityCap: cap,
    notes: values.notes,
  };
};
